import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { bucketTopN } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { isValidMetalType } from "../jewelry/_shared/isValidMetalType";

// Global's Admin_JewelryMetaltypeChartColunmData returns a typed response,
// camelCased on the wire -- "metalType"/"count"/"metalTypeID"/"metalColorID"
// (only the first letter lowercased, "ID" stays uppercase), unlike the
// Dealer stack's raw-DataTable "MetalType"/"COUNT"/... . Same
// bucketing/noise-toggle/click-to-drill pattern as JewelryMetalTypeComp.js.
const GlobalJewelryMetalTypeComp = ({ data, loading, onSliceClick }) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState("doughnut");
  const [hideNoise, setHideNoise] = useState(true);
  const chartRef = useRef();
  const list = data || [];

  const filteredList = useMemo(
    () => (hideNoise ? list.filter((item) => isValidMetalType(item.metalType)) : list),
    [list, hideNoise]
  );
  const hiddenCount = list.length - filteredList.length;

  const { chartData, colors } = bucketTopN(filteredList, {
    labelKey: "metalType",
    valueKey: "count",
    chartLabel: "Metal Type",
    othersLabel: t("Others"),
  });
  const rows = chartData.slice(1);

  const handleClick = (event) => {
    if (!onSliceClick || !chartRef.current) return;
    const [el] = getElementAtEvent(chartRef.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (!label || label === t("Others")) return;
    const row = filteredList.find((item) => item.metalType === label);
    if (row) onSliceClick({ metalTypeId: row.metalTypeID, metalColorId: row.metalColorID });
  };

  const chartJsData = {
    labels: rows.map((row) => row[0]),
    datasets: [
      {
        data: rows.map((row) => row[1]),
        backgroundColor: colors,
        borderColor: "#fff",
        borderWidth: chartType === "doughnut" ? 2 : 0,
        borderRadius: chartType === "bar" ? 4 : 0,
      },
    ],
  };

  return (
    <div className="edr-panel mt-4">
      <div className="edr-panel-head">
        <label>
          <b> {t("Jewelry Metal Type Clicks")} </b>
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div className="edr-toggle">
            <span>{t("Hide None / unspecified")}</span>
            <label className="edr-switch">
              <input type="checkbox" checked={hideNoise} onChange={(e) => setHideNoise(e.target.checked)} />
              <span className="edr-switch-track"></span>
            </label>
            {hiddenCount > 0 && <span className="edr-badge edr-flag">{hiddenCount} {t("hidden")}</span>}
          </div>
          <div className="edr-seg">
            <button className={chartType === "doughnut" ? "edr-on" : ""} onClick={() => setChartType("doughnut")}>{t("Donut")}</button>
            <button className={chartType === "bar" ? "edr-on" : ""} onClick={() => setChartType("bar")}>{t("Bar")}</button>
          </div>
        </div>
      </div>
      <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
        {!loading && rows.length > 0 ? (
          <>
            {chartType === "doughnut" && <CustomLegend chartData={chartData} colorData={colors} />}
            <div className="edr-chartwrap edr-tall">
              {chartType === "doughnut" ? (
                <Doughnut ref={chartRef} data={chartJsData} options={donutOptions} onClick={handleClick} />
              ) : (
                <Bar ref={chartRef} data={chartJsData} options={barOptions(true)} onClick={handleClick} />
              )}
            </div>
          </>
        ) : (
          <ChartState loading={loading} />
        )}
      </div>
    </div>
  );
};

export default GlobalJewelryMetalTypeComp;
